"use client";

import { Component, type ReactNode } from "react";

interface Props {
  children: ReactNode;
  /** Shown in place of the crashed subtree. */
  fallbackLabel?: string;
}

interface State {
  error: Error | null;
}

/**
 * Contains render/effect errors so one misbehaving component (most likely a third-party
 * wallet adapter initialising async "after some time") can't blank the entire app with Next's
 * "This page couldn't load" screen. The rest of the UI keeps working; the failed subtree shows
 * a retry card.
 *
 * A class component because React only exposes error boundaries via the class API.
 */
class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error) {
    // Surfaced for debugging; wire to Sentry/logging here later.
    console.error("[error-boundary]", error);
  }

  reset = () => this.setState({ error: null });

  render() {
    if (this.state.error) {
      return (
        <div className="grid min-h-[200px] place-items-center p-6 text-center">
          <div className="max-w-sm">
            <p className="text-sm text-fg">
              {this.props.fallbackLabel ?? "Something went wrong here."}
            </p>
            <p className="mt-1 text-xs text-fg-muted">
              {this.state.error.message || "Unexpected error"}
            </p>
            <button
              type="button"
              onClick={this.reset}
              className="mt-3 rounded-md bg-brand px-3 py-1.5 text-xs font-medium text-black hover:bg-brand-glow"
            >
              Try again
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
